<template>
  <div class="tw-space-y-6" v-if="!isLoading">
    <!-- Header with avatar + name -->
    <div class="tw-flex tw-items-center tw-gap-4">
      <div v-if="hasAvatar" class="tw-relative">
        <img
          :src="String(mainData.logo)"
          :alt="mainData.name || 'Customer'"
          class="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-200"
        />
      </div>
      <div>
        <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900 tw-m-0">
          {{ mainData.name || t('customer_management.titles.customer_info') }}
        </h2>
      </div>
    </div>
    <!-- Main Data -->
    <MainDataForm
      :data="mainData"
      :is-edit="true"
      @onChange="handleMainDataChange"
      @onSave="saveMainData"
    />

    <!-- Team Assignment -->
    <TeamAssignmentForm
      :data="{ team }"
      :users="users"
      :is-edit="true"
      :on-save="saveTeam"
      @change="handleTeamChange"
    />

    <!-- Forms Configuration -->
    <CustomerForms
      :data="formsState"
      :is-edit="true"
      :on-save="saveForms"
      @change="handleFormsChange"
    />

    <!-- Legals -->
    <LegalsForm
      :data="legalsState"
      :is-edit="true"
      :on-save="saveLegals"
      @change="handleLegalsChange"
    />
  </div>
  <div v-else class="tw-space-y-8">
    <div class="tw-animate-pulse">
      <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-1/3 tw-mb-4"></div>
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mb-8">
        <div class="tw-h-32 tw-bg-gray-200 tw-rounded"></div>
        <div class="tw-h-32 tw-bg-gray-200 tw-rounded"></div>
        <div class="tw-h-32 tw-bg-gray-200 tw-rounded"></div>
      </div>
      <div class="tw-h-64 tw-bg-gray-200 tw-rounded tw-mb-8"></div>
      <div class="tw-h-48 tw-bg-gray-200 tw-rounded tw-mb-8"></div>
      <div class="tw-h-48 tw-bg-gray-200 tw-rounded"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useCustomerStore } from '@/stores/customer.store';
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

// New forms
import { FinancialLimitFrequency } from '@/types/customer';
import CustomerForms from '../Forms/CustomerForms.vue';
import LegalsForm from '../Forms/Legals.vue';
import MainDataForm from '../Forms/MainDataForm.vue';
import TeamAssignmentForm from '../Forms/TeamAssignmentForm.vue';

const route = useRoute();
const customerId = ref<number>(Number(route.params.id));
const customerStore = useCustomerStore();
const isLoading = ref(true);
const { setBreadcrumbs } = useBreadcrumbs();
const { t } = useI18n();

// Main data state mapped to new form API
const mainData = reactive({
  name: '',
  legalName: '',
  customerLimit: undefined as number | null | undefined,
  automaticApprovalLimit: undefined as number | null | undefined,
  financialLimitFrequency: FinancialLimitFrequency.ALL_TIME as FinancialLimitFrequency | undefined,
  address: '',
  email: '',
  phone: '',
  restrictMode: false,
  logo: null as File | string | null,
});

// Team assignment state
type UserId = string | number;

interface ClearingGroup {
  id?: number;
  name: string;
  display_order: number;
  assignees: UserId[];
}

const team = reactive({
  purchasers: [] as UserId[],
  orderClearingGroups: [] as ClearingGroup[],
  payers: [] as UserId[],
  paymentClearingGroups: [] as ClearingGroup[],
  approvers: [] as { userId: UserId; limit: number }[],
  viewers: [] as UserId[],
});
const users = ref<any[]>([]);

// Keep a lookup of current team assignments (for upsert by id)
const teamAssignmentIdByKey = ref<Record<string, number>>({});

// Forms configuration state + cached id lookups
const formsState = reactive({
  forms: {
    orderRequired: [] as number[],
    orderOptional: [] as number[],
    paymentRequired: [] as number[],
    paymentOptional: [] as number[],
  },
});
const orderAssignmentIdByField = ref<Record<number, number>>({});
const paymentAssignmentIdByField = ref<Record<number, number>>({});

// Legals state
const legalsState = reactive({
  legals: {
    legalTerms: '',
    termsAndConditions: '',
  },
});

// Loaders
const loadMainData = async () => {
  const data = await customerStore.getMainDataById(customerId.value);
  mainData.name = data.name ?? '';
  mainData.legalName = data.legal_name ?? '';
  mainData.customerLimit = data.customer_limit != null ? Number(data.customer_limit) : undefined;
  mainData.automaticApprovalLimit =
    data.auto_approval_limit != null ? Number(data.auto_approval_limit) : undefined;
  mainData.financialLimitFrequency =
    (data.financial_limit_frequency as FinancialLimitFrequency) ?? FinancialLimitFrequency.ALL_TIME;
  mainData.address = data.address ?? '';
  mainData.email = data.email ?? '';
  mainData.phone = data.phone_number ?? '';
  mainData.restrictMode = Boolean(data.is_order_and_payment_restrict);
  mainData.logo = data.avatar ?? null;

  legalsState.legals.legalTerms = data.legal_notes ?? '';
  legalsState.legals.termsAndConditions = data.terms_and_conditions ?? '';
};

const hasAvatar = computed(() => typeof mainData.logo === 'string' && !!mainData.logo);

const loadUsers = async () => {
  try {
    users.value = await customerStore.getUsers();
  } catch {
    users.value = [];
  }
};

const loadTeamAssignments = async (options?: { skipClearingGroups?: boolean }) => {
  // reset
  team.purchasers = [];
  team.payers = [];
  team.approvers = [];
  team.viewers = [];
  teamAssignmentIdByKey.value = {};

  if (!options?.skipClearingGroups) {
    team.orderClearingGroups = [];
    team.paymentClearingGroups = [];
  }

  // Load user role assignments
  const res = await customerStore.getAssignTeamById({
    customer: customerId.value,
    is_active: true,
  });
  (res.results || []).forEach((a: any) => {
    const uid = a.user?.id ?? a.user;
    const key = `${uid}:${a.user_role}`;
    if (a.id) teamAssignmentIdByKey.value[key] = a.id;

    switch (a.user_role) {
      case 'PURCHASER':
        team.purchasers.push(uid);
        break;
      case 'PAYER':
        team.payers.push(uid);
        break;
      case 'APPROVER':
        team.approvers.push({ userId: uid, limit: Number(a.approver_limit) || 0 });
        break;
      case 'VIEWER':
        team.viewers.push(uid);
        break;
    }
  });

  // Load clearing groups (skip after save — local state is already correct)
  if (!options?.skipClearingGroups) {
    try {
      const clearingGroupsRes = await customerStore.getClearingGroups(customerId.value);
      team.orderClearingGroups = clearingGroupsRes.order_groups || [];
      team.paymentClearingGroups = clearingGroupsRes.payment_groups || [];
    } catch (e) {
      // If clearing groups don't exist yet (e.g., for older customers), initialize with defaults
      console.warn('Failed to load clearing groups, using defaults:', e);
      team.orderClearingGroups = [
        { display_order: 1, name: 'Group A', assignees: [] },
        { display_order: 2, name: 'Group B', assignees: [] },
        { display_order: 3, name: 'Group C', assignees: [] },
        { display_order: 4, name: 'Group D', assignees: [] },
      ];
      team.paymentClearingGroups = [
        { display_order: 1, name: 'Group A', assignees: [] },
        { display_order: 2, name: 'Group B', assignees: [] },
        { display_order: 3, name: 'Group C', assignees: [] },
        { display_order: 4, name: 'Group D', assignees: [] },
      ];
    }
  }
};

const loadForms = async () => {
  // reset
  formsState.forms.orderRequired = [];
  formsState.forms.orderOptional = [];
  formsState.forms.paymentRequired = [];
  formsState.forms.paymentOptional = [];
  orderAssignmentIdByField.value = {};
  paymentAssignmentIdByField.value = {};

  const orderRes = await customerStore.getFormOrderById({ customer: customerId.value });
  (orderRes.results || []).forEach((o: any) => {
    const fieldId = o.field?.id ?? o.field;
    if (o.id) orderAssignmentIdByField.value[fieldId] = o.id;
    if (o.is_mandatory) {
      formsState.forms.orderRequired.push(fieldId);
    } else {
      formsState.forms.orderOptional.push(fieldId);
    }
  });

  const paymentRes = await customerStore.getFormPaymentById({ customer: customerId.value });
  (paymentRes.results || []).forEach((p: any) => {
    const fieldId = p.field?.id ?? p.field;
    if (p.id) paymentAssignmentIdByField.value[fieldId] = p.id;
    if (p.is_mandatory) {
      formsState.forms.paymentRequired.push(fieldId);
    } else {
      formsState.forms.paymentOptional.push(fieldId);
    }
  });
};

onMounted(async () => {
  await Promise.all([loadMainData(), loadUsers()]);
  await Promise.all([loadTeamAssignments(), loadForms()]);
  // Set breadcrumbs: Customers > {Customer Name} > Edit Customer
  setBreadcrumbs([
    {
      labelKey: 'customer_management.titles.customers',
      to: { name: 'customers' },
    },
    {
      label: mainData.name || t('customer_management.titles.customer_info'),
      to: { name: 'view-customer', params: { id: String(customerId.value) } },
    },
    {
      labelKey: 'customer_management.actions.edit',
    },
  ]);
  isLoading.value = false;
});

// Handlers: onChange events
const handleMainDataChange = (updates: Partial<typeof mainData>) => {
  Object.assign(mainData, updates);
};

const handleTeamChange = (updates: { team: typeof team }) => {
  Object.assign(team, updates.team);
};

const handleFormsChange = (updates: typeof formsState) => {
  Object.assign(formsState.forms, updates.forms);
};

const handleLegalsChange = (updates: typeof legalsState) => {
  Object.assign(legalsState.legals, updates.legals);
};

// Save actions per section
const saveMainData = async () => {
  const payload: any = {
    name: mainData.name,
    legal_name: mainData.legalName,
    customer_limit: mainData.customerLimit,
    auto_approval_limit: mainData.automaticApprovalLimit,
    financial_limit_frequency: mainData.financialLimitFrequency,
    address: mainData.address,
    email: mainData.email,
    phone_number: mainData.phone,
    is_order_and_payment_restrict: mainData.restrictMode,
    avatar: mainData.logo, // map logo -> avatar for API
  };
  // Do not send avatar if it's an URL/string or null (unchanged)
  if (typeof payload.avatar === 'string' || payload.avatar == null) {
    delete payload.avatar;
  }
  try {
    await customerStore.updateMainData(payload, customerId.value);
  } catch (e) {
    // Swallow; SectionSaveButton handles UI state; errors can be toasts elsewhere
    console.error(e);
  }
};

const saveTeam = async (): Promise<{ success: boolean; message: string }> => {
  const rows: any[] = [];
  const pushRows = (list: UserId[], role: string, limitById?: Record<string, number>) => {
    list.forEach(uid => {
      const key = `${uid}:${role}`;
      const row: any = {
        user: uid,
        customer: customerId.value,
        user_role: role,
        is_role_default: true,
        approver_limit: null,
      };
      if (limitById && limitById[String(uid)] != null) {
        row.approver_limit = limitById[String(uid)];
      }
      if (teamAssignmentIdByKey.value[key]) row.id = teamAssignmentIdByKey.value[key];
      rows.push(row);
    });
  };

  pushRows(team.purchasers, 'PURCHASER');
  pushRows(team.payers, 'PAYER');
  const approverLimits: Record<string, number> = {};
  team.approvers.forEach(a => (approverLimits[String(a.userId)] = Number(a.limit) || 0));
  pushRows(
    team.approvers.map(a => a.userId),
    'APPROVER',
    approverLimits
  );
  pushRows(team.viewers, 'VIEWER');

  try {
    await customerStore.createAssignTeam({
      customer: customerId.value,
      assignments: rows,
      order_groups: team.orderClearingGroups.map(g => ({
        display_order: g.display_order,
        name: g.name,
        assignees: g.assignees as number[],
      })),
      payment_groups: team.paymentClearingGroups.map(g => ({
        display_order: g.display_order,
        name: g.name,
        assignees: g.assignees as number[],
      })),
    });

    // Refresh assignment IDs for future saves (skip clearing groups —
    // local state is already correct and avoids read-after-write race)
    await loadTeamAssignments({ skipClearingGroups: true });
    return { success: true, message: 'Team assignment saved successfully' };
  } catch (e) {
    console.error(e);
    return { success: false, message: 'Failed to save team assignment' };
  }
};

const saveForms = async (): Promise<{ success: boolean; message: string }> => {
  const orderData: any[] = [];
  const paymentData: any[] = [];

  const addOrder = (fieldId: number, mandatory: boolean) => {
    const row: any = {
      customer: customerId.value,
      field: fieldId,
      is_mandatory: mandatory,
    };
    const id = orderAssignmentIdByField.value[fieldId];
    if (id) row.id = id;
    orderData.push(row);
  };
  const addPayment = (fieldId: number, mandatory: boolean) => {
    const row: any = {
      customer: customerId.value,
      field: fieldId,
      is_mandatory: mandatory,
    };
    const id = paymentAssignmentIdByField.value[fieldId];
    if (id) row.id = id;
    paymentData.push(row);
  };

  formsState.forms.orderRequired.forEach(id => addOrder(id, true));
  formsState.forms.orderOptional.forEach(id => addOrder(id, false));
  formsState.forms.paymentRequired.forEach(id => addPayment(id, true));
  formsState.forms.paymentOptional.forEach(id => addPayment(id, false));

  try {
    await customerStore.createFormOrder(orderData);
    await customerStore.createFormPayment(paymentData);
    // Refresh to capture ids
    await loadForms();
    return { success: true, message: 'Form configuration saved successfully' };
  } catch (e) {
    console.error(e);
    return { success: false, message: 'Failed to save form configuration' };
  }
};

const saveLegals = async (): Promise<{ success: boolean; message: string }> => {
  const payload = {
    legal_notes: legalsState.legals.legalTerms,
    terms_and_conditions: legalsState.legals.termsAndConditions,
  } as any;
  try {
    await customerStore.createLegal(payload, customerId.value);
    return { success: true, message: 'Legal terms saved successfully' };
  } catch (e) {
    console.error(e);
    return { success: false, message: 'Failed to save legal terms' };
  }
};
</script>

<style scoped></style>
